import { Line } from "vue-chartjs";
import { ChartConfig } from "@/config/chart.js";
import { APP_COLORS } from "@/config/app-colors";
import "chartjs-adapter-moment";
import { ReviewCategoriesChartConfigMixin } from "@/helpers/mixins/review-categories-chart-config.js";
import { getSentimentBetweenRange } from "@/helpers/helpers";
import { pluralCount } from "@/helpers/helpers";

export default {
  extends: Line,
  mixins: [ReviewCategoriesChartConfigMixin],
  props: {
    maxValue: {
      type: Number
    },
    minValue: {
      type: Number
    }
  },
  computed: {
    chartOptions() {
      const vm = this;
      return {
        onHover: function(event, elements) {
          if (!(elements && elements.length)) {
            const tooltipEl = document.getElementById("chartjs-tooltip");
            if (!tooltipEl) return;
            tooltipEl.style.opacity = 0;
          }
        },
        tooltips: {
          enabled: false,
          mode: "nearest",
          custom: function(tooltipModel) {
            if (!tooltipModel.dataPoints) return;
            const tooltipData = vm.data;

            let tooltipEl = document.getElementById("chartjs-tooltip");
            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.innerHTML =
                "<div style='color: white; border-radius: 20px; background:rgba(54, 60, 86, 0.7); padding: 18px;'></div>";
              document.body.appendChild(tooltipEl);
            }

            // Set caret Position
            tooltipEl.classList.remove("above", "below", "no-transform");
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add("no-transform");
            }

            // Set Text
            if (tooltipModel.body) {
              const titleLines = tooltipModel.title || [];
              let innerHtml = "";

              tooltipModel.dataPoints.forEach((dataPoint, index) => {
                const currentDataSet = dataPoint.datasetIndex;
                const basicDataPointIndex = dataPoint.index;
                const data1 =
                  currentDataSet === 0
                    ? tooltipData.tooltip.basic[basicDataPointIndex]
                    : tooltipData.tooltip.comparison[basicDataPointIndex];
                innerHtml += createTitleHtml(titleLines, currentDataSet);
                innerHtml += `<div style="margin-bottom: ${
                  tooltipModel.dataPoints.length > 1 && index === 0 ? 24 : 0
                }px;">
                <div><b>${
                  vm.selectedCategory.name
                }: <span style="margin-left: 8px;">${pluralCount(
                  "message.pluralForms.review",
                  data1.count
                )}</span></b></div>
                <div style="height: 4px;"></div>
                <div>
                  <div style="display: flex; flex-direction: row;">
                  ${vm.$t("message.reviews.categories.sentiment")}:
                  <div style="display: flex; flex-direction: column; margin-left: 8px;">
                    <b>${getSentimentBetweenRange(+dataPoint.value)}</b>
                    
                   ${dataPoint.value} pkt
                  </div>
                  </div>
                </div>
                </div>
                </div>`;
              });

              const tableRoot = tooltipEl.querySelector("div");
              tableRoot.innerHTML = innerHtml;
            }

            function createTitleHtml(titleLines, datasetIndex) {
              const periodLabel =
                datasetIndex === 0
                  ? vm.$t("message.reviews.categories.selectedPeriod")
                  : vm.$t("message.reviews.categories.comparedPeriod");
              return titleLines
                .map(
                  (title, index) =>
                    `<div style="margin-bottom: 8px;">${periodLabel}: <b>${
                      title.split(";")[datasetIndex]
                    }</b></div>`
                )
                .join("");
            }

            // `this` will be the overall tooltip
            const position = this._chart.canvas.getBoundingClientRect();

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = "absolute";

            const distanceToRight =
              window.innerWidth -
              (position.left + window.pageXOffset + tooltipModel.caretX);

            if (distanceToRight < tooltipEl.offsetWidth) {
              const tooltipLeft =
                position.left +
                window.pageXOffset +
                tooltipModel.caretX -
                tooltipEl.offsetWidth;
              tooltipEl.style.left = `${tooltipLeft}px`;
            } else {
              const tooltipLeft =
                position.left + window.pageXOffset + tooltipModel.caretX;

              tooltipEl.style.left = `${tooltipLeft}px`;
            }
            const tooltipTop =
              position.top + window.pageYOffset + tooltipModel.caretY;
            tooltipEl.style.top = `${tooltipTop}px`;

            tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
            tooltipEl.style.fontSize = `${tooltipModel.bodyFontSize}px`;
            tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
            tooltipEl.style.padding =
              tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
            tooltipEl.style.pointerEvents = "none";
          }
        },
        layout: {
          padding: {
            top: 10
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: true,
                drawBorder: false
              },
              ticks: {
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: 0,
                fontFamily: ChartConfig.fontFamily,
                fontColor: ChartConfig.fontColor,
                beginAtZero: true,

                ...(this.maxValue && { max: this.maxValue }),
                ...(this.minValue && { min: this.minValue })
              },
              display: true
            }
          ],
          xAxes: [
            {
              id: "x",
              type: "category",
              gridLines: {
                display: this.gridLines,
                drawBorder: false
              },

              ticks: {
                maxTicksLimit: 18,
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: 0,
                backdropPadding: 0,
                fontFamily: ChartConfig.fontFamily,
                fontColor: this.$route.query.compareOption
                  ? APP_COLORS.PRIMARY
                  : ChartConfig.fontColor,
                callback: value => this.tickCallback(value, 0)
              },
              display: true
            },
            {
              id: "x1",
              type: "category",
              display: !!this.$route.query.compareOption,
              gridLines: {
                display: this.gridLines,
                drawBorder: false
              },
              ticks: {
                maxTicksLimit: 18,
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: 0,
                backdropPadding: 0,
                fontFamily: ChartConfig.fontFamily,
                callback: value => this.tickCallback(value, 1)
              }
            }
          ]
        }
      };
    }
  },

  methods: {
    tickCallback(value, index) {
      const result = value.split(";")[index];

      return result.length > 10 ? `${result.substr(0, 10)}...` : result;
    },
    commonDatasetConfig(color, data) {
      return {
        borderColor: color,
        borderWidth: 3,
        pointBorderColor: color,
        pointBackgroundColor: color,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: color,
        pointBorderWidth: 3,
        pointHoverRadius: 3,
        pointHoverBorderWidth: 3,
        pointRadius: 3,
        fill: true,
        clip: 3,
        backgroundColor: "transparent",
        spanGaps: true,
        data
      };
    },
    render() {
      this.renderChart(
        {
          labels: this.labels,
          datasets: [
            {
              ...this.commonDatasetConfig(APP_COLORS.PRIMARY, this.data.basic),
              xAxisID: "x"
            },
            {
              ...this.commonDatasetConfig(
                APP_COLORS.BlUE_GRAY,
                this.data.comparison
              ),
              xAxisID: "x1"
            }
          ]
        },
        this.chartOptions
      );
    }
  }
};
